import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

import { ChevronDownIcon } from '../atoms/icons.js';
import { cn } from '../lib/cn.js';

/*
 * Bordered button styled as a Popover trigger: optional leading slot,
 * caller-supplied label content, trailing ChevronDownIcon. Pair with
 * `<PopoverTrigger asChild>` to anchor a Popover.
 *
 * `size="md"` matches the full-width sidebar switchers (studio, game).
 * `size="sm"` matches the inline topbar selector (milestone).
 */
const popoverButtonVariants = cva(
  // Chrome (sidebar/topbar) overrides surface-2/3 to translucent whites so a
  // resting bg-surface-2 + hover bg-surface-3 matches the prototype's
  // rgba(255,255,255,0.06) -> 0.1 lift naturally. Leave the opacity at full
  // here so the variant translates cleanly if it is ever rendered against a
  // non-chrome surface.
  [
    'items-center gap-2 rounded-md',
    'border border-border-strong bg-surface-2 text-text',
    'transition-colors hover:bg-surface-3',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      size: {
        md: 'flex w-full px-2.5 py-2 text-left text-sm',
        sm: 'inline-flex px-2.5 py-1 text-xs',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type PopoverButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'type'
> &
  VariantProps<typeof popoverButtonVariants> & {
    leading?: ReactNode;
  };

export const PopoverButton = forwardRef<HTMLButtonElement, PopoverButtonProps>(
  ({ className, size, leading, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(popoverButtonVariants({ size }), className)}
      {...props}
    >
      {leading}
      {children}
      <ChevronDownIcon className="text-text-muted" />
    </button>
  ),
);
PopoverButton.displayName = 'PopoverButton';

export { popoverButtonVariants };
